import React, { useState, useContext, useEffect } from 'react';
import { AppProvider, AppContext } from './context/AppContext';
import Navigation from './components/Navigation';
import Header from './components/Header';
import Login from './components/Login';
import WebmailTab from './tabs/WebmailTab';
import LeaveExpenseTab from './tabs/LeaveExpenseTab';
import AdminDashboard from './tabs/AdminDashboard/AdminDashboard';
import AddressBookTab from './tabs/AddressBookTab';
import UserSettingsTab from './tabs/UserSettingsTab';
import SystemSettingsModal from './tabs/AdminDashboard/SystemSettingsModal';
import DashboardTab from './tabs/DashboardTab';
import LeaveApprovalManagement from './tabs/AdminDashboard/LeaveApprovalManagement';
import ContractTab from './tabs/ContractTab';
import PublicContractSignView from './components/PublicContractSignView';
import MobileApp from './mobile/MobileApp';
import { useDeviceMode } from './hooks/useDeviceMode';
import { initNativeBridge } from './services/nativeBridge';
import { Lock, ShieldAlert, KeyRound, Check } from 'lucide-react';

function AppContent() {
  const { isMobile, viewMode, setManualViewMode } = useDeviceMode();
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' | 'webmail' | 'leave' | 'expense' | 'contract' | 'admin' | 'approval' | 'addressbook' | 'settings'
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('clou_sidebar_open');
    if (saved !== null) return saved === 'true';
    return window.innerWidth >= 1024;
  });
  const [isSystemSettingsOpen, setIsSystemSettingsOpen] = useState(false);

  const handleSetSidebarOpen = (val) => {
    setSidebarOpen(prev => {
      const next = typeof val === 'function' ? val(prev) : val;
      localStorage.setItem('clou_sidebar_open', String(next));
      return next;
    });
  };
  
  const { 
    isLoggedIn, 
    currentUser, 
    mustChangePasswordPrompt, 
    setMustChangePasswordPrompt, 
    completePasswordChange,
    deptLeads,
    secondaryApproverIds
  } = useContext(AppContext);

  // Check if current user is an approver (dept lead or secondary approver)
  const isApprover = currentUser && (
    Object.values(deptLeads || {}).includes(currentUser.UserID) || 
    (secondaryApproverIds || []).includes(currentUser.UserID)
  );

  // Mandatory initial password change state
  const [firstNewPw, setFirstNewPw] = useState('');
  const [firstConfirmPw, setFirstConfirmPw] = useState('');
  const [firstPwError, setFirstPwError] = useState('');

  const isMasterAdmin = currentUser && (
    currentUser.UserID === 'clou0315' || 
    currentUser.Account === 'clou0315' || 
    (currentUser.Email && currentUser.Email.toLowerCase().startsWith('clou0315@'))
  );

  const [mailComposeInitial, setMailComposeInitial] = useState(null);

  const handleComposeTo = (recipientEmail) => {
    setMailComposeInitial({
      to: recipientEmail,
      isComposing: true
    });
    setActiveTab('webmail');
  };

  // Security & visibility guard: STAFF users cannot access or view the ADMIN/APPROVAL/DYNAMODB tabs without permission
  useEffect(() => {
    if (activeTab === 'admin' && currentUser?.Role !== 'ADMIN') {
      setActiveTab('dashboard');
    }
    if (activeTab === 'approval' && currentUser?.Role !== 'ADMIN' && !isApprover) {
      setActiveTab('dashboard');
    }
  }, [currentUser, activeTab, isApprover]);

  // Listen to system settings trigger from sidebar action
  useEffect(() => {
    const handleSystemSettings = () => {
      setIsSystemSettingsOpen(true);
    };
    const handleGlobalCompose = (e) => {
      const detail = e?.detail || {};
      setMailComposeInitial({
        to: detail.to || '',
        cc: detail.cc || '',
        bcc: detail.bcc || '',
        subject: detail.subject || '',
        body: detail.body || '',
        attachments: detail.attachments || [],
        isComposing: true
      });
      setActiveTab('webmail');
    };
    window.addEventListener('trigger-system-settings', handleSystemSettings);
    window.addEventListener('trigger-compose', handleGlobalCompose);
    return () => {
      window.removeEventListener('trigger-system-settings', handleSystemSettings);
      window.removeEventListener('trigger-compose', handleGlobalCompose);
    };
  }, []);

  // Initialize Capacitor Native Bridge (Back Button, Push, Splash)
  useEffect(() => {
    initNativeBridge({
      onBackPress: () => {
        if (isSystemSettingsOpen) {
          setIsSystemSettingsOpen(false);
          return true;
        }
        if (activeTab !== 'dashboard') {
          setActiveTab('dashboard');
          return true;
        }
        return false;
      }
    });
  }, [isSystemSettingsOpen, activeTab]);

  const handleFirstPasswordSubmit = (e) => {
    e.preventDefault();
    if (firstNewPw.length < 8) {
      setFirstPwError('새 비밀번호는 8자 이상이어야 합니다.');
      return;
    }
    if (firstNewPw === '1234' || firstNewPw === 'clou1234') {
      setFirstPwError('초기 기본 비밀번호(clou1234)와 동일한 비밀번호는 사용하실 수 없습니다.');
      return;
    }
    if (firstNewPw !== firstConfirmPw) {
      setFirstPwError('새 비밀번호와 확인 값이 일치하지 않습니다.');
      return;
    }

    completePasswordChange(currentUser.UserID, firstNewPw);
    setFirstNewPw('');
    setFirstConfirmPw('');
    setFirstPwError('');
    alert('비밀번호가 성공적으로 변경되었습니다. 이제 모든 서비스를 안전하게 이용하실 수 있습니다.');
  };

  // Check for public contract signing link via URL hash or search params
  const [publicToken, setPublicToken] = useState(() => {
    const hash = window.location.hash || '';
    if (hash.includes('token=')) {
      const match = hash.match(/token=([a-zA-Z0-9_-]+)/);
      if (match && match[1]) return match[1];
    }
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || null;
  });

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash || '';
      if (hash.includes('token=')) {
        const match = hash.match(/token=([a-zA-Z0-9_-]+)/);
        if (match && match[1]) setPublicToken(match[1]);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // If public token link is accessed, render PublicContractSignView directly without login requirement
  if (publicToken) {
    return <PublicContractSignView token={publicToken} />;
  }

  if (!isLoggedIn) {
    return <Login />;
  }

  // 1. Render Dedicated Mobile App if Mobile mode is active
  if (isMobile) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
        <MobileApp onSwitchToDesktop={() => setManualViewMode('desktop')} />

        {/* System Settings Modal overlay */}
        <SystemSettingsModal
          isOpen={isSystemSettingsOpen}
          onClose={() => setIsSystemSettingsOpen(false)}
        />

        {/* Mandatory First-Login Password Change Modal */}
        {mustChangePasswordPrompt && (
          <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-xs flex justify-center items-center p-4">
            <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 text-left">
              <div className="bg-indigo-50 px-6 py-5 border-b border-indigo-100 flex items-center gap-3">
                <div className="p-2.5 bg-indigo-600 text-white rounded-2xl shadow-sm">
                  <KeyRound className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-base md:text-lg font-bold text-slate-900">초기 비밀번호 변경 필수</h2>
                  <p className="text-xs text-indigo-700 font-medium">안전한 계정 사용을 위해 새 비밀번호를 설정하세요.</p>
                </div>
              </div>

              <form onSubmit={handleFirstPasswordSubmit} className="p-6 space-y-4">
                <div className="bg-amber-50 border border-amber-200 p-3.5 rounded-2xl text-xs text-amber-800 leading-relaxed flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p>
                    관리자가 발급한 <strong>초기/임시 비밀번호</strong>로 로그인되었습니다. 사내 정보보호 정책에 따라 첫 로그인 시 새 비밀번호를 필히 설정해야 합니다.
                  </p>
                </div>

                {firstPwError && (
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs font-bold text-rose-700">
                    {firstPwError}
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">새 비밀번호 (8자 이상)</label>
                  <input
                    type="password"
                    placeholder="새 비밀번호 입력"
                    value={firstNewPw}
                    onChange={(e) => setFirstNewPw(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 font-bold"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">새 비밀번호 확인</label>
                  <input
                    type="password"
                    placeholder="새 비밀번호 재입력"
                    value={firstConfirmPw}
                    onChange={(e) => setFirstConfirmPw(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 font-bold"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-sm shadow-md transition-all cursor-pointer mt-2"
                >
                  비밀번호 변경 및 계속하기
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // 2. Render Full PC Desktop Layout
  return (
    <div className="min-h-screen bg-[#F4F5F9] text-slate-900 flex flex-row font-sans text-sm md:text-base">
      {/* 1. Left Dual Sidebar Navigation Panel (64px Rail + 240px Sidebar) */}
      <Navigation
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isOpen={sidebarOpen}
        setIsOpen={handleSetSidebarOpen}
      />

      {/* 2. Right Main Layout Wrapper (Header + Dynamic Main Canvas) */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden bg-[#F4F5F9]">
        {/* Top Header Tool Belt */}
        <Header
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={handleSetSidebarOpen}
          onSwitchToMobile={() => setManualViewMode('mobile')}
        />

        {/* Dynamic Page content Canvas */}
        <main className="flex-1 overflow-y-auto bg-[#F4F5F9] text-slate-900">
          {activeTab === 'dashboard' && <DashboardTab setActiveTab={setActiveTab} />}
          {activeTab === 'webmail' && (
            <WebmailTab 
              initialCompose={mailComposeInitial} 
              onClearInitialCompose={() => setMailComposeInitial(null)} 
            />
          )}
          {activeTab === 'leave' && <LeaveExpenseTab mode="leave" />}
          {activeTab === 'expense' && <LeaveExpenseTab mode="expense" />}
          {activeTab === 'contract' && <ContractTab />}
          {activeTab === 'admin' && currentUser?.Role === 'ADMIN' && <AdminDashboard />}
          {activeTab === 'approval' && (currentUser?.Role === 'ADMIN' || isApprover) && (
            <div className="p-4 md:p-8 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6">연차 및 경비 결재 승인 처리</h2>
              <LeaveApprovalManagement />
            </div>
          )}
          {activeTab === 'addressbook' && <AddressBookTab onComposeTo={handleComposeTo} />}
          {activeTab === 'settings' && <UserSettingsTab />}
        </main>
      </div>

      {/* System Settings Modal overlay */}
      <SystemSettingsModal
        isOpen={isSystemSettingsOpen}
        onClose={() => setIsSystemSettingsOpen(false)}
      />

      {/* Mandatory First-Login Password Change Modal (Requirement 1) */}
      {mustChangePasswordPrompt && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-xs flex justify-center items-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 text-left">
            <div className="bg-indigo-50 px-6 py-5 border-b border-indigo-100 flex items-center gap-3">
              <div className="p-2.5 bg-indigo-600 text-white rounded-2xl shadow-sm">
                <KeyRound className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-base md:text-lg font-bold text-slate-900">초기 비밀번호 변경 필수</h2>
                <p className="text-xs text-indigo-700 font-medium">안전한 계정 사용을 위해 새 비밀번호를 설정하세요.</p>
              </div>
            </div>

            <form onSubmit={handleFirstPasswordSubmit} className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 p-3.5 rounded-2xl text-xs text-amber-800 leading-relaxed flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p>
                  관리자가 발급한 <strong>초기/임시 비밀번호</strong>로 로그인되었습니다. 사내 정보보호 정책에 따라 첫 로그인 시 새 비밀번호를 필히 설정해야 합니다.
                </p>
              </div>

              {firstPwError && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs font-bold text-rose-700">
                  {firstPwError}
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase">새 비밀번호 (8자 이상) *</label>
                <input
                  type="password"
                  placeholder="영문, 숫자 포함 8자 이상"
                  value={firstNewPw}
                  onChange={e => setFirstNewPw(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 text-slate-900"
                  required
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase">새 비밀번호 확인 *</label>
                <input
                  type="password"
                  placeholder="새 비밀번호를 다시 입력하세요"
                  value={firstConfirmPw}
                  onChange={e => setFirstConfirmPw(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 text-slate-900"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-xl shadow-md transition-all cursor-pointer flex items-center justify-center gap-2 mt-2"
              >
                <Check className="w-4 h-4" />
                비밀번호 변경 및 서비스 시작
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;
